import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Loader from '@/components/common/Loader';
import { useToast } from '@/hooks/use-toast';
import {
  parseSignatories,
  type OrgContractDocumentItem,
} from '@/modules/org/contracts/types/contract-document.types';
import { useGetContractDocumentEvents } from '@/modules/org/contracts/hooks/use-contract-document-events';
import type { DocumentStatus } from '@/modules/org/contracts/constants/mock-contract-documents';

import { ReviewShell } from '@/modules/templates/components/review/ReviewShell';
import { ReviewSidebar } from '@/modules/templates/components/review/ReviewSidebar';
import { SidebarFooter } from '@/modules/templates/components/review/SidebarFooter';
import { MobileActionBar } from '@/modules/templates/components/review/MobileActionBar';
import { PdfPreviewToolbar } from '@/modules/templates/components/review/PdfPreviewToolbar';
import { FinalizedPdfViewer } from '@/modules/templates/components/review/FinalizedPdfViewer';
import TemplatePDFReviewHeader from '@/modules/templates/components/review/TemplatePDFReviewHeader';
import { ReviewMessage } from '@/modules/templates/components/review/ReviewMessage';

import { buildFieldsFromSignatories, colorForIndex } from './signature-fields';
import { parseDocxDocument } from './document-payload';
import { useContractPdfUrl } from './useContractPdfUrl';

/** US-Letter, in points — the base the viewer scales from. */
const BASE_PAGE_WIDTH_PT = 612;
const BASE_PAGE_HEIGHT_PT = 792;

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2;

interface DocxLockedReviewProps {
  contract: OrgContractDocumentItem;
  documentId: string;
  slug: string;
}

/**
 * Read-only review for a fixed-DOCX contract that can no longer change.
 *
 * Uses the same shell, header, sidebar and footer as every other review — only
 * the viewer differs, showing a finished PDF instead of an editable document.
 * Nothing here places or edits signatories: the contract is locked, so the
 * sidebar reports their status and the document is simply displayed.
 */
export default function DocxLockedReview({
  contract,
  documentId,
  slug,
}: DocxLockedReviewProps) {
  const navigate = useNavigate();
  const { toast } = useToast();

  const document = useMemo(() => parseDocxDocument(contract.Document), [contract.Document]);
  const {
    url: pdfUrl,
    error: pdfError,
    isLoading: isResolvingPdf,
  } = useContractPdfUrl({ document, sigDocumentUrl: contract.SigDocumentUrl, slug });

  const [scale, setScale] = useState(1);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarTab, setSidebarTab] = useState('details');
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  // Separate from `isDownloading` — rasterising every page is much slower than
  // the plain fetch, so the two buttons report progress independently.
  const [isDownloadingProtected, setIsDownloadingProtected] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0].contentRect.width;
      setContainerWidth(width - (width < 640 ? 32 : 48));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const pageWidth =
    containerWidth && containerWidth < BASE_PAGE_WIDTH_PT ? containerWidth : BASE_PAGE_WIDTH_PT;
  const pageHeight = (pageWidth / BASE_PAGE_WIDTH_PT) * BASE_PAGE_HEIGHT_PT;

  const signatories = useMemo(() => parseSignatories(contract.Signatories), [contract.Signatories]);

  const sidebarSignatories = useMemo(
    () =>
      signatories.map((s, i) => ({
        name: s.name,
        email: s.email,
        role: s.title ?? '',
        color: colorForIndex(i),
      })),
    [signatories]
  );

  // Where each signature ended up, for the sidebar's page map. Display only.
  const signatureFields = useMemo(
    () => buildFieldsFromSignatories(signatories),
    [signatories]
  );

  const { data: eventsResult } = useGetContractDocumentEvents(documentId, !!documentId);
  const signatoryEventByEmail = useMemo(() => {
    const map = new Map<string, { eventType: string; occurredAt: string; signedTime?: string }>();
    (eventsResult?.items ?? []).forEach((e) => {
      if (
        (e.EventType !== 'signatory_signed_success' && e.EventType !== 'signatory_signed_failed') ||
        !e.SignatoryEmail
      ) {
        return;
      }
      const existing = map.get(e.SignatoryEmail);
      const occurredAt = e.OccurredAt ?? e.CreatedDate ?? '';
      if (!existing || occurredAt > existing.occurredAt) {
        map.set(e.SignatoryEmail, {
          eventType: e.EventType,
          occurredAt,
          signedTime: e.SignedTime,
        });
      }
    });
    return map;
  }, [eventsResult?.items]);

  const handleDownload = useCallback(async () => {
    if (!pdfUrl || isDownloading) return;
    // A signed contract can be a large PDF over a slow link, so the fetch is
    // worth reporting even though no conversion is involved.
    setIsDownloading(true);
    try {
      const response = await fetch(pdfUrl);
      if (!response.ok) throw new Error(`Failed to fetch PDF: ${response.status}`);
      const objectUrl = URL.createObjectURL(await response.blob());
      const link = window.document.createElement('a');
      link.href = objectUrl;
      link.download = `${slug || 'document'}.pdf`;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error('Download failed:', err);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Could not download this document.',
      });
    } finally {
      setIsDownloading(false);
    }
  }, [pdfUrl, slug, toast, isDownloading]);

  /**
   * Same PDF, rebuilt as images so no text can be extracted from it. The
   * contract is locked, so `pdfUrl` already is the authoritative document —
   * there is nothing to re-convert.
   */
  const handleProtectedDownload = useCallback(async () => {
    if (!pdfUrl || isDownloadingProtected) return;
    setIsDownloadingProtected(true);
    try {
      const response = await fetch(pdfUrl);
      if (!response.ok) throw new Error(`Failed to fetch PDF: ${response.status}`);
      // Imported on demand: this pulls in pdfjs plus the react-pdf renderer.
      const { buildProtectedPdf } = await import('@/lib/pdf/protected-pdf');
      const protectedBlob = await buildProtectedPdf(await response.blob());
      const objectUrl = URL.createObjectURL(protectedBlob);
      const link = window.document.createElement('a');
      link.href = objectUrl;
      link.download = `${slug || 'document'}-protected.pdf`;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error('Protected download failed:', err);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Could not prepare a protected copy of this document.',
      });
    } finally {
      setIsDownloadingProtected(false);
    }
  }, [pdfUrl, slug, toast, isDownloadingProtected]);

  const scrollToPage = useCallback((page: number) => {
    containerRef.current
      ?.querySelector(`[data-page-number="${page}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const sidebar = (
    <ReviewSidebar
      sidebarTab={sidebarTab}
      onTabChange={setSidebarTab}
      // Locked: no Edit tab, no block editing.
      isEditable={false}
      isPremiumUnlocked
      editedBlockIds={new Set()}
      details={{ title: contract.TemplateTitle || contract.Name || 'Document' }}
      signatureFields={signatureFields}
      numPages={numPages}
      signatories={sidebarSignatories}
      signatoryEventByEmail={signatoryEventByEmail}
      currentPage={1}
      onPageClick={scrollToPage}
      editTabScrollRef={null}
      isEditing={false}
      editingBlock={null}
      isSavingEdit={false}
      isEditingEffectiveDate={false}
      onSaveEdit={async () => undefined}
      onCancelEdit={() => undefined}
      onEditorDirtyChange={() => undefined}
      onEditorTextChange={() => undefined}
      outline={[]}
      onEditBlock={() => undefined}
      openSections={{}}
      onToggleSection={() => undefined}
      isFixedDocx={true}
    />
  );

  if (isResolvingPdf) return <Loader />;
  if (pdfError) return <ReviewMessage>{pdfError}</ReviewMessage>;
  if (!pdfUrl) return <ReviewMessage>This document has no PDF to preview.</ReviewMessage>;

  return (
    <ReviewShell
      scrollContainerRef={containerRef}
      sidebarOpen={sidebarOpen}
      onSidebarOpenChange={setSidebarOpen}
      sidebar={sidebar}
      sidebarFooter={
        <SidebarFooter
          canUndo={false}
          canRedo={false}
          onUndo={() => undefined}
          onRedo={() => undefined}
          isEditable={false}
          signatureFieldCount={signatureFields.length}
          isPremiumUnlocked
          onSendToSignature={() => undefined}
          onRequestDownload={() => void handleDownload()}
          onRequestProtectedDownload={() => void handleProtectedDownload()}
          isDownloading={isDownloading}
          isDownloadingProtected={isDownloadingProtected}
          isPendingApproval={
            contract.HasSentForApproval === true && contract.IsApproved !== true
          }
          isContractApproved={contract.IsApproved === true}

        />
      }
      header={
        <TemplatePDFReviewHeader
          templateTitle={contract.TemplateTitle || contract.Name || 'Document'}
          numPages={numPages}
          signatureFields={signatureFields}
          signatoriesLength={signatories.length}
          hasUnsavedChanges={false}
          isAuthenticated
          onBack={() => navigate(-1)}
          canLetterhead={false}
          onLetterheadClick={() => undefined}
          setShowDownloadPrompt={() => void handleDownload()}
          handleSendToSignature={() => undefined}
          onSaveDraft={() => undefined}
          saveDraftStatus="idle"
          canSaveDraft={false}
          isEditable={false}
          documentStatus={contract.Status as DocumentStatus | undefined}
          isContractApproved={contract.IsApproved === true}
          hasSentForApproval={contract.HasSentForApproval === true}
          handleDownloadPdf={() => void handleDownload()}
          isDownloading={isDownloading}
        />
      }
      toolbar={
        <PdfPreviewToolbar
          scale={scale}
          isPdfStale={false}
          isLoadingPdf={false}
          onRegenerateNow={() => undefined}
          onZoomIn={() => setScale((z) => Math.min(MAX_ZOOM, Math.round((z + 0.1) * 100) / 100))}
          onZoomOut={() => setScale((z) => Math.max(MIN_ZOOM, Math.round((z - 0.1) * 100) / 100))}
          onFitPage={() => setScale(1)}
          onFitWidth={() => setScale(1)}
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen((o) => !o)}
        />
      }
      mobileActionBar={
        <MobileActionBar
          isEditable={false}
          signatureFieldCount={signatureFields.length}
          onRequestDownload={() => void handleDownload()}
          onSendToSignature={() => undefined}
          isDownloading={isDownloading}
          isPendingApproval={
            contract.HasSentForApproval === true && contract.IsApproved !== true
          }
        />
      }
      viewer={
        <FinalizedPdfViewer
          fileUrl={pdfUrl}
          numPages={numPages}
          scale={scale}
          pageWidth={pageWidth}
          pageHeight={pageHeight}
          onLoadSuccess={({ numPages: n }) => setNumPages(n)}
        />
      }
    />
  );
}
